import { useTranslation } from "react-i18next";
import { useState } from "react";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";

interface EmojiPickerProps {
  selectedEmoji: string;
  onEmojiSelect: (emoji: string) => void;
}

const EMOJI_OPTIONS = [
  // Stars & Sparkles
  "⭐", "🌟", "✨", "💫", "⚡", "🔥", "💎", "💰", "🎯", "🎪",
  "🌠", "💥", "☄️", "🌀", "🔮", "🪄", "✴️", "🔆", "🔅", "💢",
  
  // Hearts & Love
  "❤️", "💛", "💚", "💙", "💜", "🧡", "💗", "💖", "💕", "💝",
  "🖤", "🤍", "🤎", "💟", "❣️", "💔", "❤️‍🔥", "❤️‍🩹", "♥️", "💘",
  
  // Trophies & Achievements
  "🏆", "🥇", "🥈", "🥉", "🏅", "🎖️", "👑", "💪", "🦾", "🦿",
  "🎗️", "📛", "🔰", "⚜️", "🏵️", "🎫", "🎟️", "🏷️", "🔖", "🎀",
  
  // Fitness & Sports Activities
  "🏃", "🏋️", "🚴", "🧘", "🤸", "🏊", "🧗", "🤾", "⛹️", "🚣",
  "🏃‍♀️", "🏃‍♂️", "🚶", "🚶‍♀️", "🚶‍♂️", "🧎", "🧍", "🤺", "🏇", "⛸️",
  "🤼", "🤽", "🤾‍♀️", "🤾‍♂️", "🤹", "🧗‍♀️", "🧗‍♂️", "🏌️", "🏄", "🏄‍♀️",
  
  // Ball Sports
  "⚽", "🏀", "🏈", "⚾", "🎾", "🏐", "🏉", "🎱", "🏓", "🏸",
  "🥎", "🥏", "🪀", "🪃", "🥍", "🏑", "🏒", "🥌", "🎳", "🧿",
  
  // Combat & Winter Sports
  "🥊", "🥋", "⛳", "🎣", "🥏", "🪂", "🛹", "⛷️", "🏂", "🤿",
  "🎿", "🛷", "🛼", "🩰", "🪘", "🏹", "🔫", "🪃", "🪁", "🎽",
  
  // Fruits
  "🍎", "🍊", "🍋", "🍌", "🍉", "🍇", "🍓", "🫐", "🍒", "🍑",
  "🥭", "🍍", "🥥", "🥝", "🍐", "🍏", "🫒", "🫑", "🍅", "🍆",
  
  // Vegetables & Healthy Food
  "🥑", "🥦", "🥕", "🌽", "🥒", "🥬", "🥗", "🥙", "🥪", "🌮",
  "🫛", "🫚", "🧄", "🧅", "🥔", "🍠", "🫘", "🌶️", "🫙", "🥜",
  
  // Protein & Meals
  "🍗", "🍖", "🥩", "🍔", "🍟", "🍕", "🌭", "🥓", "🥞", "🧇",
  "🥘", "🍲", "🫕", "🍛", "🍜", "🍝", "🍣", "🍤", "🍱", "🥡",
  
  // Breakfast & Dairy
  "🍳", "🥚", "🧈", "🧀", "🥛", "☕", "🍵", "🧃", "🧋", "🥤",
  "🫖", "🍶", "🍺", "🍻", "🥃", "🍷", "🍸", "🍹", "🧉", "🫗",
  
  // Drinks & Snacks
  "💧", "🍯", "🍞", "🥖", "🥨", "🥯", "🧁", "🍰", "🎂", "🍪",
  "🍿", "🧂", "🍫", "🍬", "🍭", "🍮", "🍩", "🥧", "🍡", "🍧",
  
  // Nature & Plants
  "🌱", "🌿", "🍀", "🌾", "🌻", "🌺", "🌸", "🌼", "🌷", "🌹",
  "🪴", "🪻", "🪷", "🌵", "🎋", "🎍", "🍃", "🍂", "🍁", "🪹",
  
  // Weather & Sky
  "🌈", "☀️", "⛅", "🌤️", "⛈️", "🌩️", "❄️", "☃️", "🌊", "🌲",
  "🌙", "🌛", "🌜", "🌝", "🌞", "🪐", "🌍", "🌎", "🌏", "🌕",
  
  // Technology & Devices
  "📱", "💻", "⌨️", "🖱️", "🖥️", "⌚", "📷", "📸", "🎥", "🎬",
  "📡", "🔌", "💾", "💿", "📀", "🧮", "📟", "📠", "🔦", "🔭",
  
  // Education & Office
  "📚", "📖", "📝", "✏️", "🖊️", "📊", "📈", "📉", "🗂️", "📁",
  "📕", "📗", "📘", "📙", "📓", "📒", "📃", "📄", "📑", "🗒️",
  "🎒", "🧳", "💼", "📂", "🗃️", "🗄️", "🗑️", "📎", "🖇️", "📐",
  
  // Tools & Building
  "🔨", "🔧", "⚙️", "🧰", "🪛", "🔩", "⛏️", "🔋", "🪫", "💡",
  "🪚", "🪓", "🔗", "⛓️", "🪝", "🧲", "🪜", "🧱", "🪨", "🪵",
  
  // Arts & Music
  "🎨", "🖌️", "🖍️", "✍️", "🎭", "🎪", "🎬", "🎤", "🎧", "🎵",
  "🎶", "🎸", "🎹", "🥁", "🎺", "🎻", "🪕", "🎮", "🕹️", "🎲",
  "🪗", "🪘", "🪇", "🪈", "🎷", "🎼", "🎙️", "🎚️", "🎛️", "📻",
  
  // Money & Finance
  "💵", "💴", "💶", "💷", "💳", "💹", "💱", "💲", "🪙", "🏦",
  "🏧", "💸", "🧾", "📋", "📌", "📍", "🗝️", "🔐", "🔏", "🔒",
  
  // Time & Calendar
  "⏰", "⏱️", "⏲️", "⌛", "⏳", "📅", "📆", "🗓️", "🕐", "🕑",
  "🕒", "🕓", "🕔", "🕕", "🕖", "🕗", "🕘", "🕙", "🕚", "🕛",
  
  // Celebration & Party
  "🎉", "🎊", "🎈", "🎁", "🎀", "🎃", "🎄", "🎆", "🎇", "🧨",
  "🪅", "🪆", "🎑", "🎍", "🎎", "🎏", "🎐", "🎋", "🧧", "🎠",
  
  // Drinks & Celebration
  "🍾", "🥂", "🍻", "🥳", "🤩", "😎", "🥰", "😍", "🤗", "😊",
  
  // Transport - Air & Rail
  "🚀", "✈️", "🚁", "🚂", "🚃", "🚄", "🚅", "🚆", "🚇", "🚈",
  "🛸", "🛩️", "🛫", "🛬", "🪂", "💺", "🚉", "🚊", "🚝", "🚞",
  
  // Transport - Road
  "🚲", "🛴", "🛹", "🏍️", "🚗", "🚙", "🚌", "🚎", "🏎️", "🚓",
  "🛵", "🛺", "🚕", "🚖", "🚘", "🚚", "🚛", "🚜", "🛻", "🚐",
  
  // Animals - Mammals
  "🐱", "🐶", "🐺", "🦁", "🐯", "🦊", "🐻", "🐼", "🐨", "🐹",
  "🐭", "🐰", "🦄", "🐴", "🦓", "🦌", "🐮", "🐷", "🐸", "🐒",
  "🦍", "🦧", "🐘", "🦛", "🦏", "🐪", "🐫", "🦒", "🦘", "🦬",
  
  // Animals - Birds & Sea
  "🐰", "🦄", "🐴", "🐸", "🐢", "🦎", "🐍", "🐙", "🦑", "🦐",
  "🦋", "🐝", "🐞", "🦗", "🕷️", "🦂", "🐚", "🦀", "🦞", "🦆",
  "🦅", "🦉", "🦜", "🐦", "🐧", "🕊️", "🦩", "🦚", "🦃", "🐓",
  "🐋", "🐳", "🦈", "🐬", "🦭", "🐟", "🐠", "🐡", "🦐", "🦑",
  
  // Faces & Emotions
  "😀", "😃", "😄", "😁", "😆", "😅", "🤣", "😂", "🙂", "🙃",
  "😉", "😊", "😇", "🥹", "😌", "😍", "🥰", "😘", "😗", "😙",
  "🤔", "🤨", "😐", "😑", "😶", "🙄", "😏", "😣", "😥", "😮",
  "🤯", "😱", "🥵", "🥶", "😳", "🤪", "😵", "🫠", "🤠", "🥸",
  "😎", "🤓", "🧐", "😤", "😠", "🤬", "😈", "👿", "💀", "☠️",
  "🤡", "👹", "👺", "👻", "👽", "👾", "🤖", "🎃", "😺", "😸",
  
  // Gestures & Body
  "👍", "👎", "👊", "✊", "🤛", "🤜", "👏", "🙌", "👐", "🤲",
  "🤝", "🙏", "✌️", "🤞", "🤟", "🤘", "🤙", "👈", "👉", "👆",
  "👇", "☝️", "👋", "🤚", "🖐️", "✋", "🖖", "👌", "🤌", "🤏",
  "💅", "🦶", "🦵", "🦿", "💪", "🦾", "👂", "🦻", "👃", "🧠",
  "🫀", "🫁", "🦷", "🦴", "👀", "👁️", "👅", "👄", "💋", "🩸",
  
  // People & Roles
  "👶", "🧒", "👦", "👧", "🧑", "👱", "👨", "🧔", "👩", "🧓",
  "👴", "👵", "🙍", "🙎", "🙅", "🙆", "💁", "🙋", "🧏", "🙇",
  "🤦", "🤷", "👮", "🕵️", "💂", "🥷", "👷", "🤴", "👸", "👳",
  "👲", "🧕", "🤵", "👰", "🤰", "🫃", "🤱", "👼", "🎅", "🤶",
  "🦸", "🦹", "🧙", "🧚", "🧛", "🧜", "🧝", "🧞", "🧟", "🧌",
  
  // Symbols & Status
  "✅", "✔️", "❌", "❎", "⭕", "🔴", "🟠", "🟡", "🟢", "🔵",
  "🟣", "🟤", "⚫", "⚪", "🔶", "🔷", "🔸", "🔹", "💠", "🔔",
  "🔕", "📣", "📢", "💬", "💭", "🗯️", "🎓", "🎖️", "🏅", "👍",
  "⚠️", "🚫", "⛔", "📛", "🔞", "☢️", "☣️", "➕", "➖", "➗",
  "✖️", "♾️", "❓", "❔", "❕", "❗", "💯", "🔝", "🔙", "🔛",
  
  // Arrows & Directions
  "⬆️", "⬇️", "⬅️", "➡️", "↗️", "↘️", "↙️", "↖️", "↕️", "↔️",
  "↩️", "↪️", "⤴️", "⤵️", "🔄", "🔃", "🔀", "🔁", "🔂", "▶️",
  
  // Miscellaneous Objects
  "🛡️", "⚔️", "🗡️", "🔪", "🪓", "🔫", "🪃", "🏹", "🛠️", "🧲",
  "🪬", "📿", "🧿", "🪯", "♠️", "♥️", "♦️", "♣️", "🃏", "🀄",
  "🎴", "🔮", "🧿", "🪄", "🧸", "🪆", "🖼️", "🧵", "🪡", "🧶",
  "👓", "🕶️", "🥽", "🥼", "🦺", "👔", "👕", "👖", "🧣", "🧤",
  "🧥", "🧦", "👗", "👘", "🥻", "🩱", "🩲", "🩳", "👙", "👚",
  "👛", "👜", "👝", "🛍️", "🎒", "🩴", "👞", "👟", "🥾", "🥿",
  "👠", "👡", "🩰", "👢", "👑", "👒", "🎩", "🎓", "🧢", "🪖",
  "⛑️", "💄", "💍", "💎", "🔇", "🔈", "🔉", "🔊", "📯", "🔔",
  
  // Medical & Health
  "💊", "💉", "🩹", "🩺", "🩻", "🩼", "🏥", "🚑", "🧬", "🔬",
  "🔭", "🩸", "🧪", "🧫", "🧴", "🧹", "🧺", "🧻", "🚽", "🚿",
  
  // Buildings & Places
  "🏠", "🏡", "🏢", "🏣", "🏤", "🏥", "🏦", "🏨", "🏩", "🏪",
  "🏫", "🏬", "🏭", "🏯", "🏰", "💒", "🗼", "🗽", "⛪", "🕌",
  "🛕", "🕍", "⛩️", "🕋", "⛲", "⛺", "🌁", "🌃", "🏙️", "🌄",
  "🌅", "🌆", "🌇", "🌉", "🌌", "🎠", "🎡", "🎢", "🚂", "⛱️"
];

const EmojiPicker = ({
 selectedEmoji, onEmojiSelect }: EmojiPickerProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const handleEmojiSelect = (emoji: string) => {
    onEmojiSelect(emoji);
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="icon" className="h-10 w-10 text-xl">
          {selectedEmoji}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-[90vw] sm:max-w-md p-0">
        <DialogHeader className="px-4 pt-4 pb-2">
          <DialogTitle>{t('ui.text.select_emoji')}</DialogTitle>
        </DialogHeader>
        <div 
          className="overflow-y-auto overflow-x-hidden px-4 pb-4"
          style={{ 
            maxHeight: 'calc(80vh - 60px)',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          <div className="grid grid-cols-7 sm:grid-cols-10 gap-1">
            {EMOJI_OPTIONS.map((emoji) => (
              <button
                key={emoji}
                type="button"
                className="h-12 w-12 flex items-center justify-center text-2xl rounded-md hover:bg-accent transition-colors active:scale-95 touch-manipulation"
                onClick={() => handleEmojiSelect(emoji)}
              >
                {emoji}
              </button>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EmojiPicker;
